import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import { DreamMarkdownReader } from "@portal/app/_components/ui-elements/DreamMarkdown/DreamMarkdownReader";
import { ValidatedFormikField } from "@portal/app/_components/ui-elements/formHelpers";
import { logger } from "@portal/app/_common/lib/logger";
import { isProduction } from "@portal/app/_common/lib/edgeUtils";
import { captureMessage } from "@sentry/nextjs";
import { useCallback, useMemo } from "react";
import { renderToString } from "react-dom/server";

import { renderPhoneNumber } from "@portal/app/_common/lib/formatRenderers";
import { resolveTemplateVariables } from "../../utils/resolveTemplateVariables";

const getDefaultDisclaimerText = (schoolName?: string) =>
  `By clicking this checkbox, I give permission for ${schoolName || "the school"} to contact me about my interest in their class either through Dreambound or by text, phone, or email.`;

interface FormFunnelDisclaimerProps {
  disclaimer?: string | null;
  advertiserName: string;
  isTrustedFormEnabled: boolean;
  contact: {
    phone: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  };
  schoolName?: string;
  answers?: Record<string, unknown>;
}

/**
 * Builds a merged answers map that includes common aliases for customer data
 * fields so CMS authors can use any of `{{phone_number}}`, `{{phone}}`,
 * `{{PHONE}}`, etc. in disclaimer templates and have them resolve correctly.
 *
 * Priority: explicit answers values are preserved; aliases only fill gaps.
 */
function buildAnswersWithAliases(
  answers: Record<string, unknown>,
  contact: FormFunnelDisclaimerProps["contact"]
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...answers };

  // Helper: set a key only if not already present
  const setIfMissing = (key: string, value: unknown) => {
    if (value != null && value !== "" && merged[key] == null) {
      merged[key] = value;
    }
  };

  // Phone aliases
  const phoneValue = contact.phone || answers["PHONE"] || answers["phone"];
  setIfMissing("phone", phoneValue);
  setIfMissing("phone_number", phoneValue);
  setIfMissing("PHONE", phoneValue);

  // First name aliases
  const firstNameValue =
    contact.firstName || answers["NAME_FIRST"] || answers["first_name"] || answers["firstName"];
  setIfMissing("first_name", firstNameValue);
  setIfMissing("firstName", firstNameValue);
  setIfMissing("NAME_FIRST", firstNameValue);

  // Last name aliases
  const lastNameValue =
    contact.lastName || answers["NAME_LAST"] || answers["last_name"] || answers["lastName"];
  setIfMissing("last_name", lastNameValue);
  setIfMissing("lastName", lastNameValue);
  setIfMissing("NAME_LAST", lastNameValue);

  // Email aliases
  const emailValue = contact.email || answers["EMAIL"] || answers["email"];
  setIfMissing("email", emailValue);
  setIfMissing("EMAIL", emailValue);

  return merged;
}

/**
 * Consent disclaimer component for the form funnel hosted flow.
 *
 * This is a standalone version of SchoolDisclaimer that does NOT depend on
 * FlowContext or useMixpanel. It renders the same consent UI (checkbox,
 * contact methods, disclaimer text) and preserves all TrustedForm
 * data-tf-element-role attributes required for compliance.
 */
export const FormFunnelDisclaimer = ({
  disclaimer,
  advertiserName,
  isTrustedFormEnabled,
  contact,
  schoolName,
  answers,
}: FormFunnelDisclaimerProps) => {
  const isCustomDisclaimer = !!disclaimer;
  const rawDisclaimer = disclaimer || getDefaultDisclaimerText(schoolName);
  // Always build the alias map from contact so tokens like {{phone_number}}
  // resolve even when a caller omits `answers`. `buildAnswersWithAliases` is
  // null-safe, and `resolveTemplateVariables` leaves unmatched tokens as-is.
  const mergedAnswers = useMemo(
    () => buildAnswersWithAliases(answers ?? {}, contact),
    [answers, contact]
  );
  const finalDisclaimer = resolveTemplateVariables(rawDisclaimer, mergedAnswers);

  /**
   * Pre-render the disclaimer markdown and inject a TrustedForm
   * consent-advertiser-name span around the advertiser name when a
   * custom disclaimer is provided.
   *
   * Uses useMemo (not useState initializer) so the HTML re-renders when
   * finalDisclaimer changes -- e.g., when template tokens like {{phone_number}}
   * are resolved against updated answers.
   */
  const disclaimerHtml = useMemo(() => {
    const html = renderToString(<StyledDreamMarkdownReader value={finalDisclaimer} />);
    if (!advertiserName || !isCustomDisclaimer) return html;
    const safeAdvertiserName = advertiserName
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
    return html.replace(
      safeAdvertiserName,
      `<span data-tf-element-role="consent-advertiser-name">${safeAdvertiserName}</span>`
    );
  }, [finalDisclaimer, advertiserName, isCustomDisclaimer]);

  const checkConsentTags = useCallback(() => {
    // Only perform TrustedForm validation for custom disclaimers
    if (!isTrustedFormEnabled || !isCustomDisclaimer) {
      return;
    }
    /**
     * TrustedForm requires several HTML attributes to be present on the page
     * for consent tags to be considered properly installed.
     * https://developers.activeprospect.com/docs/trustedform/consent-tagging/
     */
    const consentLanguage = document.querySelector('[data-tf-element-role="consent-language"]');
    const consentAdvertiserName = document.querySelector(
      '[data-tf-element-role="consent-advertiser-name"]'
    );
    const contactMethod = document.querySelector('[data-tf-element-role="contact-method"]');
    const consentGrantorPhone = document.querySelector(
      '[data-tf-element-role="consent-grantor-phone"]'
    );
    const consentSubmitButton = document.querySelector('[data-tf-element-role="submit"]');
    const consentOfferButton = document.querySelector('[data-tf-element-role="offer"]');
    const elements = [
      [consentLanguage, "consent-language"],
      [consentAdvertiserName, "consent-advertiser-name"],
      [contactMethod, "contact-method"],
      [consentGrantorPhone, "consent-grantor-phone"],
      [consentSubmitButton, "submit"],
      [consentOfferButton, "offer"],
    ];
    if (
      !consentLanguage ||
      !consentAdvertiserName ||
      !contactMethod ||
      !consentGrantorPhone ||
      !consentSubmitButton ||
      !consentOfferButton
    ) {
      const message = `TrustedForm consent language is not properly configured! Missing: ${elements
        .filter(([element]) => !element)
        .map(([, name]) => name)
        .join(", ")}`;
      logger.error(message);
      if (isProduction()) {
        captureMessage(message, { level: "error" });
      }
    }
  }, [isTrustedFormEnabled, isCustomDisclaimer]);

  return (
    <DisclaimerBox data-testid="hosted-consent-card">
      <ValidatedFormikField
        id="leadid_tcpa_disclosure"
        name="disclaimer"
        as={Checkbox}
        sx={{
          p: 0,
          // Stay above 44px even at the smallest (13px) root font size.
          minWidth: "3.5rem",
          minHeight: "3.5rem",
          flexShrink: 0,
          color: "primary.main",
        }}
        disableFocusRipple
        disableTouchRipple
        onMouseDown={() => checkConsentTags()}
      />
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0, pt: "1rem" }}
        data-tf-element-role={isCustomDisclaimer ? "consent-language" : undefined}
      >
        <ContactMethods contact={contact} />
        <Typography component="label" htmlFor="leadid_tcpa_disclosure" variant="disclaimer">
          <span dangerouslySetInnerHTML={{ __html: disclaimerHtml }} />
        </Typography>
      </Box>
    </DisclaimerBox>
  );
};

/**
 * Displays the user's phone number with TrustedForm attributes.
 * Unlike SchoolDisclaimer's ContactMethods, this version does NOT include
 * an edit button because the form funnel does not use FlowContext/goToStep.
 */
const ContactMethods = ({ contact }: { contact: { phone: string | null } }) => {
  return contact.phone ? (
    <Box data-tf-element-role="contact-method" data-testid="hosted-consent-phone">
      <Typography
        sx={{
          color: "text.primary",
          fontSize: "0.95rem",
          fontWeight: 700,
          lineHeight: 1.3,
        }}
      >
        Phone:{" "}
        <span data-tf-element-role="consent-grantor-phone">{renderPhoneNumber(contact.phone)}</span>
      </Typography>
    </Box>
  ) : null;
};

const DisclaimerBox = styled(Box)`
  display: flex;
  align-items: flex-start;

  gap: 0.75rem;
  padding: 1rem;

  border: 1px solid ${({ theme }) => theme.palette.dream.borderColor};
  border-radius: 20px;
  background: ${({ theme }) => theme.palette.dream.grayLighter};

  cursor: pointer;

  & .editor-paragraph {
    margin: 0;
  }
`;

const StyledDreamMarkdownReader = styled(DreamMarkdownReader)`
  font-size: 0.8rem;
`;
